import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, ShieldCheck, Mail, KeyRound, Layers, Radar, Zap } from "lucide-react";

const heroImage = "https://images.unsplash.com/photo-1631679706909-1844bbd07221?crop=entropy&cs=srgb&fm=jpg";
const typeImages = {
  aparthotel: "https://images.pexels.com/photos/7031722/pexels-photo-7031722.jpeg",
  student: "https://images.pexels.com/photos/3959919/pexels-photo-3959919.jpeg",
  senior: "https://images.unsplash.com/photo-1643376452350-97eadd2c417f?crop=entropy&cs=srgb&fm=jpg",
};

const KEYWORDS = ["Tarifs négociés", "Clé en main", "Gestion professionnelle", "Demande unique", "Tout le marché", "Réponses rapides"];
const HUB_POINTS = [
  { Icon: Layers, t: "Centralisez vos recherches", d: "Toutes vos demandes et recherches d'appartements réunies au même endroit, suivies de bout en bout." },
  { Icon: Radar, t: "Interrogez tout le marché", d: "Un point d'entrée unique pour solliciter l'ensemble des résidences partenaires, sans multiplier les démarches." },
  { Icon: Zap, t: "Des réponses rapides et adaptées", d: "Vos demandes sont adressées automatiquement aux bons interlocuteurs, selon vos critères." },
];

export default function Landing() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-[#F5F3ED]">
      <header className="bg-[#F5F3ED]/90 backdrop-blur-md border-b border-[#E5E1D8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-serif text-2xl tracking-tight text-[#2B3029]">matchappart</span>
            <span className="text-[10px] tracking-[0.25em] uppercase text-[#8C6C50] font-semibold mt-1 hidden sm:inline">privé</span>
          </div>
          <nav className="flex items-center gap-3 sm:gap-4">
            <Link to="/demande" className="text-sm text-[#8C6C50] font-medium hover:text-[#6B5B49] transition-colors whitespace-nowrap" data-testid="landing-custom-request-link">Sur mesure</Link>
            <Link to="/login" className="text-sm hover:text-[#8C6C50] whitespace-nowrap hidden sm:inline" data-testid="landing-login-link">Se connecter</Link>
            <Link
              to="/register"
              className="btn-primary px-4 sm:px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap"
              data-testid="landing-register-link"
            >
              <span className="sm:hidden">S'inscrire</span><span className="hidden sm:inline">Créer mon compte</span>
            </Link>
          </nav>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <div className="text-xs tracking-[0.25em] uppercase font-semibold text-[#8C6C50] mb-6">
            Tarifs négociés · point d'entrée unique · plateforme privée
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#1A1A1A] leading-[1.05]">
            Des <em className="italic text-[#8C6C50]">tarifs négociés</em><br />
            sur des hébergements<br />
            clé en main.
          </h1>
          <p className="mt-8 text-base lg:text-lg text-[#666] leading-relaxed max-w-xl">
            matchappart négocie en amont avec les résidences pour vous obtenir des conditions
            privilégiées — sur des logements meublés, gérés par des professionnels, avec des
            niveaux de service adaptés à votre budget. Une seule demande suffit pour interroger tout le
            marché : elle est transmise aux bonnes résidences pour des réponses rapides. L'accès au
            catalogue est réservé aux membres pré-approuvés par l'administrateur.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            {user ? (
              <Link
                to="/catalog"
                className="btn-primary px-7 py-3.5 rounded-full text-sm font-medium inline-flex items-center gap-2"
                data-testid="landing-catalog-cta"
              >
                Voir le catalogue <ArrowRight className="w-4 h-4" />
              </Link>
            ) : (
              <>
                <Link
                  to="/register"
                  className="btn-primary px-7 py-3.5 rounded-full text-sm font-medium inline-flex items-center gap-2"
                  data-testid="landing-register-cta"
                >
                  Activer mon accès <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/login" className="text-sm underline underline-offset-4 decoration-[#8C6C50] text-[#2B3029]" data-testid="landing-login-cta">
                  J'ai déjà un compte
                </Link>
              </>
            )}
            <Link to="/demande" className="text-sm underline underline-offset-4 decoration-[#8C6C50] text-[#8C6C50] font-medium" data-testid="landing-hero-request-cta">
              Faire une demande sur mesure
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-2 md:hidden" data-testid="landing-keywords-mobile">
            {KEYWORDS.map((k) => (
              <span key={k} className="px-3 py-1.5 rounded-full text-xs font-medium border bg-white border-[#E5E1D8] text-[#2B3029]">{k}</span>
            ))}
          </div>
        </div>
        <div className="lg:col-span-6 relative">
          <div className="aspect-[4/5] rounded-sm overflow-hidden shadow-sm">
            <img src={heroImage} alt="Intérieur chaleureux" className="w-full h-full object-cover" />
          </div>
          <div className="absolute -bottom-8 -left-8 bg-white p-6 w-80 border border-[#E5E1D8] shadow-sm hidden md:block" data-testid="landing-keywords-card">
            <div className="text-xs tracking-[0.2em] uppercase text-[#8C6C50] font-semibold">L'essentiel</div>
            <div className="mt-4 flex flex-wrap gap-2">
              {KEYWORDS.map((k, i) => (
                <span
                  key={k}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border ${i === 0 ? "bg-[#8C6C50] border-[#8C6C50] text-white" : "bg-[#F5F3ED] border-[#E5E1D8] text-[#2B3029]"}`}
                >
                  {k}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-[#E5E1D8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="text-xs tracking-[0.25em] uppercase font-semibold text-[#8C6C50] mb-4">
            L'avantage matchappart
          </div>
          <h2 className="font-serif text-3xl lg:text-4xl tracking-tight max-w-2xl text-[#1A1A1A]">
            Des tarifs négociés en amont, pour chaque séjour, pour chaque budget.
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { k: "senior", t: "Tarifs négociés", d: "matchappart négocie en amont avec chaque résidence — vous accédez à des conditions que vous ne trouverez pas en direct." },
              { k: "aparthotel", t: "Clé en main", d: "Logements meublés, prêts à vivre dès le premier jour. Pas de dépôt, pas d'ameublement, pas de gestion." },
              { k: "student", t: "Gestion professionnelle", d: "Des résidences opérées par des gestionnaires expérimentés, pour une qualité de service constante." },
            ].map((c) => (
              <div key={c.k} className="group">
                <div className="aspect-[4/3] overflow-hidden mb-5">
                  <img
                    src={typeImages[c.k]}
                    alt={c.t}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <h3 className="font-serif text-2xl text-[#1A1A1A]">{c.t}</h3>
                <p className="mt-2 text-sm text-[#666] leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#2B3029] text-[#F5F3ED]" data-testid="landing-hub-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs tracking-[0.25em] uppercase font-semibold text-[#C9A887] mb-4">Un seul point d'entrée</div>
            <h2 className="font-serif text-3xl lg:text-4xl tracking-tight">
              Une demande, tout le marché, des réponses rapides.
            </h2>
            <p className="mt-6 text-sm text-[#F5F3ED]/70 leading-relaxed max-w-md">
              matchappart centralise vos demandes et vos recherches d'appartements : vous décrivez votre besoin une fois,
              nous l'adressons aux résidences qui y correspondent.
            </p>
            <Link to="/demande" className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#F5F3ED] text-[#2B3029] text-sm font-medium hover:bg-white transition-colors" data-testid="landing-hub-request-cta">
              Lancer ma demande <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-8">
            {HUB_POINTS.map(({ Icon, t, d }) => (
              <div key={t} className="border-t border-[#F5F3ED]/20 pt-6">
                <Icon className="w-6 h-6 text-[#C9A887]" strokeWidth={1.4} />
                <div className="font-serif text-xl mt-4">{t}</div>
                <p className="mt-2 text-sm text-[#F5F3ED]/70 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {[
            { Icon: Mail, t: "L'admin autorise votre email", d: "Seuls les emails validés par l'administrateur peuvent créer un compte." },
            { Icon: KeyRound, t: "Vous créez votre mot de passe", d: "Une inscription rapide et sécurisée depuis un lien privé." },
            { Icon: ShieldCheck, t: "Accès au catalogue complet", d: "Vous consultez les offres et formulez vos demandes de réservation." },
          ].map(({ Icon, t, d }) => (
            <div key={t}>
              <Icon className="w-7 h-7 text-[#8C6C50]" strokeWidth={1.4} />
              <div className="font-serif text-xl mt-5 text-[#1A1A1A]">{t}</div>
              <p className="mt-2 text-sm text-[#666] leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-[#E5E1D8] py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-xs text-[#666] flex flex-wrap items-center justify-between gap-4">
          <div>© 2026 matchappart — Accès privé sur invitation</div>
          <div className="font-serif italic">Vivre ailleurs, pour un temps.</div>
        </div>
      </footer>
    </div>
  );
}
